import { DataBlockVariant, useDataBlockType } from '@lmt/shared-types';
import {
  DataBlockUiEditor,
  DataBlockUiEditorProps,
} from '@lmt/unicorn-shared/app/components/measurements/DataBlockUiEditor';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import clsx from 'clsx';
import React, { FC, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonColor } from '../common/ui-kit/buttons/Button';
import { FormComponentProps } from '../global-forms/GlobalForms.types';
import { caseInformationDataBlockTypeName } from './common';

import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import localStyles from './DataBlockStatistic.module.scss';

export type CreateDataBlockStatisticProps = FormComponentProps & {
  dataBlockTypeId: number;
  patientId: number;
  caseId: number;
  visitId?: number;
};

export const CreateDataBlockStatistic: FC<CreateDataBlockStatisticProps> = (
  props,
) => {
  const {
    formType,
    formId,
    tryCompleteForm,
    dataBlockTypeId,
    loadBlocking,
    patientId,
    caseId,
    visitId,
  } = props;

  const i18next = useTranslation();
  const claims = useClaims();
  const selectedDataBlockType = useDataBlockType(dataBlockTypeId);

  const actions: DataBlockUiEditorProps['actions'] = useRef(null);

  const onCancel = useCallback(async () => {
    await loadBlocking(formId, i18next.t('data_blocks.loading'), async () => {
      await tryCompleteForm(formType, formId, async () => {
        return null;
      });
    });
  }, [formType, formId, tryCompleteForm, actions.current]);

  const onSave = useCallback(async () => {
    await loadBlocking(
      formId,
      i18next.t('data_blocks.loading_save'),
      async () => {
        await tryCompleteForm(formType, formId, async () => {
          return await actions.current?.getValues();
        }).catch((e) => console.error('CreateStatistics.onSave', e));
      },
    );
  }, [formType, formId, tryCompleteForm, actions.current]);
  return (
    <div
      className={clsx(styles.autoSizedContainer, localStyles.container)}
      data-test-id="dataBlockViewPopup"
    >
      <div className={styles.dataContainer}>
        <DataBlockUiEditor
          dataBlockTypeId={props.dataBlockTypeId}
          type={'create'}
          isEditable={claims.permissions.dataBlockManagement}
          actions={actions}
          isStatistic={true}
          patientId={patientId}
          caseId={caseId}
          visitId={visitId}
        />
      </div>
      {claims.permissions.dataBlockManagement && (
        <div className={styles.submitContainer}>
          <Button
            color={ButtonColor.Primary}
            disabled={
              selectedDataBlockType?.variant === DataBlockVariant.Case &&
              selectedDataBlockType?.value === caseInformationDataBlockTypeName
            }
            onClick={onCancel}
            title={i18next.t('data_blocks.cancel_button')}
            className={styles.submit}
          />

          <Button
            onClick={onSave}
            title={i18next.t('data_blocks.save_button')}
            className={styles.submit}
          />
        </div>
      )}
    </div>
  );
};

export default CreateDataBlockStatistic;
